// SantasAILab.jsx
// The Santa's AI Lab journey: every product shipped between 2023 and 2026, plus
// the talks and demos. This route is prerendered to static HTML (see
// scripts/prerender.js), so the full timeline ships in the initial HTML response
// and is readable by crawlers and AI screening tools that do not run JavaScript.
// The landing page links here from a single card instead of carrying the whole
// story inline.
import React, { useEffect } from 'react';
import './SantasAILab.css';
import { MILESTONES, VIDEOS } from './content';
import SiteHeader from '../../Components/SiteHeader';

const LIVE_PRODUCTS = [
    { label: 'Outside Agent', domain: 'outsideagent.ai' },
    { label: 'Stella Foster', domain: 'stellafoster.com' },
    { label: 'Form Number', domain: 'formnumber.com' },
];

const METRICS = [
    { value: '40,000+', label: 'people used Stella' },
    { value: '345K+', label: 'calls' },
    { value: '8.4M+', label: 'messages' },
    { value: '30%+', label: 'week-eight retention' },
];

const SYSTEMS = [
    {
        title: 'Web and native clients',
        body: 'Built React and TypeScript customer experiences and an operations console for conversations, tool calls, execution state, traces, failures, and human handoffs. Built Swift and SwiftUI clients for iPhone, iPad, and Mac, plus a React Native messaging client for iOS and Android with CallKit and an Apple Watch extension.',
    },
    {
        title: 'Backend and developer platform',
        body: "Built Stella's Node.js and Express backend on Google Cloud and Firestore, then migrated it to TypeScript. Built Outside Agent using tRPC, Zod, event-sourced PostgreSQL, Drizzle, pgvector, and MCP tooling for web, SMS, voice, and email.",
    },
    {
        title: 'Memory and retrieval',
        body: 'Built identity, preference, and working-memory tiers in Firestore, including AI-estimated lifetimes for temporary facts and conflict-aware updates. Stored Gemini embeddings and combined vector retrieval with keyword search.',
    },
    {
        title: 'Voice and integrations',
        body: 'Connected 15+ platforms across communications, payments, commerce, and transportation. Re-platformed production voice from Deepgram and ElevenLabs to OpenAI Realtime and then Gemini Live behind a provider interface supporting interruption and multilingual switching.',
    },
    {
        title: 'Durable execution and commerce',
        body: 'Replaced contending jobs with one durable Temporal workflow per conversation. Built a stored-value ledger, migrated it from Firestore transactions to PostgreSQL, and made Stripe and Twilio Pay actions idempotent with spend controls and two-step voice confirmation.',
    },
    {
        title: 'Cloud delivery and quality',
        body: 'Operated Docker services on Cloud Run and Cloud SQL with Pub/Sub, Cloud Tasks, Cloud Scheduler, GitHub Actions, Cloud Deploy, and Terraform. Built 1,620+ evaluation and red-team cases and gated releases with automated tests and Temporal replay.',
    },
];

const VideoCard = ({ id, title, event }) => (
    <div className="santa-video">
        <a
            className="santa-video-link"
            href={`https://www.youtube.com/watch?v=${id}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            <div className="santa-thumbnail">
                <img src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`} alt={title} />
                <span className="santa-play" aria-hidden="true">▶</span>
            </div>
        </a>
        <p className="santa-video-caption">
            <span className="santa-video-title">{title}</span>
            <span className="santa-video-event">{event}</span>
        </p>
    </div>
);

const Milestone = ({ title, date, status, summary, url, image, background }) => (
    <li className="santa-milestone">
        <span className="santa-milestone-dot" aria-hidden="true" />
        <a
            className="santa-milestone-link"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
        >
            <span className="santa-milestone-tile" style={{ backgroundColor: background }}>
                <img src={image} alt={`${title} logo`} />
            </span>
            <span className="santa-milestone-body">
                <span className="santa-milestone-meta">{`${date} · ${status}`}</span>
                <span className="santa-milestone-title">{title}</span>
                <span className="santa-milestone-summary">{summary}</span>
            </span>
        </a>
    </li>
);

const SantasAILab = () => {
    useEffect(() => {
        const previousTitle = document.title;
        const description = document.querySelector('meta[name="description"]');
        const previousDescription = description?.getAttribute('content');
        const canonical = document.querySelector('link[rel="canonical"]');
        const previousCanonical = canonical?.getAttribute('href');
        const title = "Santa's AI Lab | Illya Bakurov";
        const pageDescription = "The products and full-stack systems Illya Bakurov built at Santa's AI Lab, including Stella Foster and Outside Agent.";
        const pageUrl = 'https://ibakurov.com/santas-ai-lab';
        const metaUpdates = [
            ['meta[property="og:title"]', title],
            ['meta[property="og:description"]', pageDescription],
            ['meta[property="og:url"]', pageUrl],
            ['meta[name="twitter:title"]', title],
            ['meta[name="twitter:description"]', pageDescription],
        ].map(([selector, value]) => {
            const element = document.querySelector(selector);
            const previousValue = element?.getAttribute('content');
            element?.setAttribute('content', value);
            return [element, previousValue];
        });

        document.title = title;
        description?.setAttribute('content', pageDescription);
        canonical?.setAttribute('href', pageUrl);

        return () => {
            document.title = previousTitle;
            if (previousDescription) description?.setAttribute('content', previousDescription);
            if (previousCanonical) canonical?.setAttribute('href', previousCanonical);
            metaUpdates.forEach(([element, previousValue]) => {
                if (element && previousValue) element.setAttribute('content', previousValue);
            });
        };
    }, []);

    return (
        <div className="santa-page">
            <SiteHeader />
            <main className="santa">
                <header className="santa-header">
                    <p className="santa-eyebrow">Co-Founder and Founding Engineer · Oct 2023 to Sep 2026</p>
                    <h1>Santa's AI Lab</h1>
                    <p className="santa-intro">
                        A consumer product lab in Santa Monica, backed by Floodgate and Rainfall, in
                        search of products so good that people tell their friends about them. Eleven
                        products in under three years. Some found users, some failed fast, and each one
                        changed what we built next.
                    </p>
                    <p className="santa-intro">
                        Stella Foster was the one that found its audience: an assistant you reach by
                        calling or texting any phone, including a flip phone. Outside Agent is the
                        platform we had to build underneath it, and it outlived the lab.
                    </p>
                    <p className="santa-intro">
                        I was the primary hands-on full-stack engineer across the client applications,
                        backend services, data, integrations, reliability, and cloud delivery. The product
                        evolved from a Node.js and Firestore application into a TypeScript, PostgreSQL,
                        and Temporal platform operating across voice, SMS, web, mobile, email, and Slack.
                    </p>
                    <p className="santa-live">
                        <span>Still live: </span>
                        {LIVE_PRODUCTS.map((product, idx) => (
                            <React.Fragment key={product.domain}>
                                {idx > 0 && <span aria-hidden="true"> · </span>}
                                <a
                                    href={`https://${product.domain}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {product.label}
                                </a>
                            </React.Fragment>
                        ))}
                        <span aria-hidden="true"> · </span>
                        <a href="https://santasailab.com" target="_blank" rel="noopener noreferrer">
                            Lab archive
                        </a>
                    </p>
                </header>

                <section className="santa-metrics" aria-label="Santa's AI Lab outcomes">
                    {METRICS.map((metric) => (
                        <div key={metric.label}>
                            <strong>{metric.value}</strong>
                            <span>{metric.label}</span>
                        </div>
                    ))}
                </section>

                <section className="santa-section santa-systems">
                    <h2>The systems underneath the products</h2>
                    <div className="santa-systems-grid">
                        {SYSTEMS.map((system, index) => (
                            <article key={system.title}>
                                <span>{String(index + 1).padStart(2, '0')}</span>
                                <h3>{system.title}</h3>
                                <p>{system.body}</p>
                            </article>
                        ))}
                    </div>
                </section>

            <section className="santa-section">
                <h2>Talks and demos</h2>
                <div className="santa-videos">
                    {VIDEOS.map((video) => (
                        <VideoCard key={video.id} {...video} />
                    ))}
                </div>
                <p className="santa-press">
                    <span>Press: </span>
                    <a
                        href="https://ktla.com/news/how-a-phone-call-is-helping-seniors-use-ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        KTLA on Stella Foster, how a phone call is helping seniors use AI
                    </a>
                </p>
            </section>

            <section className="santa-section">
                <h2>The journey</h2>
                <p className="santa-section-note">
                    Newest first. Archived products still have their original sites.
                </p>
                <ol className="santa-timeline">
                    {MILESTONES.map((milestone) => (
                        <Milestone key={milestone.id} {...milestone} />
                    ))}
                </ol>
                <p className="santa-timeline-end">November 2023 · first experiment</p>
            </section>

                <section className="santa-section santa-closing">
                <h2>How it ended</h2>
                <p>
                    The lab wound down in 2026. My co-founder Jacob Catalano wrote the closing note,
                    a first-person account of what we got wrong about consumer AI and when he thinks
                    the market actually becomes ready.
                </p>
                <p>
                    <a
                        href="https://santasailab.com/closing-note/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Read the closing note on santasailab.com
                    </a>
                </p>
                </section>
            </main>
        </div>
    );
};

export default SantasAILab;
